import { Fragment, useEffect, useMemo, type ReactNode } from 'react';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { Link, useSearchParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useSentinelAnalyticsReport,
  prefetchSentinelBreakdowns,
  useSentinelBreakdowns,
  type AnalyticsQueryResult,
} from '@/hooks/use-analytics-v2';
import { cn } from '@/lib/utils';
import type { SentinelAnalyticsQuery, SentinelBreakdownRow, SentinelDimension, SentinelMetricKey } from './metric-keys';
import { useSentinelAnalyticsSelection } from './use-sentinel-selection';
import type { SentinelAnalyticsReport } from '@/types/sentinelAnalytics';
import { AccountPicker } from './account-picker';
import { AnalyticsFilters } from './analytics-filters';
import { BrandScopePicker } from '@/modules/analytics-v2/components/brand-scope-picker';
import { MAX_FILTER_VALUES, toDimensionFilters, toFilterOptions, type FilterOptions } from './dimension-filters';

/** A page-side cap kept from kim-ai-frontend's Sentinel Analytics; kim-analytics' metric routes take any range. */
const MAX_REPORTING_DAYS = 366;

const NO_OPTIONS: FilterOptions = { categories: [], subCategories: [], subCategoriesByCategory: {}, channels: [] };

export type AnalyticsDimension = 'category' | 'channel';

interface AnalyticsPageProps {
  title: string;
  /** Adds the Category | Channel tabs; the picked one is kept in `?tab=`. */
  tabs?: boolean;
  children: (query: SentinelAnalyticsQuery, dimension: AnalyticsDimension) => ReactNode;
}

function LoadingState() {
  return (
    <div role='status' aria-label='Loading analytics' className='mt-6 grid gap-4 lg:grid-cols-2'>
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className='flex h-28 flex-col rounded-3xl border border-gray-500 bg-white px-4 py-3'>
          <Skeleton className='h-4 w-32' />
          <Skeleton className='mt-auto h-7 w-20' />
        </div>
      ))}
    </div>
  );
}

function Notice({ title, detail }: { title: string; detail: string }) {
  return (
    <div className='mt-6 flex min-h-48 flex-col items-center justify-center rounded-[22px] border border-gray-300 px-6 text-center'>
      <p className='font-medium text-black-300'>{title}</p>
      <p className='mt-1 text-sm text-gray-900'>{detail}</p>
    </div>
  );
}

function LoadError({ title, retry }: { title: string; retry: () => void }) {
  return (
    <div role='alert' className='mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3'>
      <div>
        <p className='text-sm font-medium text-black-300'>{title}</p>
        <p className='text-xs text-gray-900'>Try again in a moment.</p>
      </div>
      <Button size='sm' onClick={retry}>Try again</Button>
    </div>
  );
}

/** Loading, error and empty states around a page's data; dims while a new pick loads. */
function AnalyticsResult<T>({ result, isEmpty, children }: { result: AnalyticsQueryResult<T>; isEmpty: (data: T) => boolean; children: (data: T) => ReactNode }) {
  let body: ReactNode;
  if (result.isError) body = <LoadError title='Analytics could not be loaded' retry={result.refetch} />;
  else if (result.data === undefined) body = <LoadingState />;
  else if (isEmpty(result.data)) body = <Notice title='No tickets in this range' detail='Choose a wider date range or clear a filter.' />;
  else body = children(result.data);
  return <div aria-busy={result.isFetching} className={cn('transition-opacity', result.isFetching && 'opacity-50')}>{body}</div>;
}

/** The overview cards' report for `query`. */
export function SentinelReport({ query, children }: { query: SentinelAnalyticsQuery; children: (report: SentinelAnalyticsReport) => ReactNode }) {
  const report = useSentinelAnalyticsReport(query);
  return <AnalyticsResult result={report} isEmpty={(data) => data.tickets.totalTickets === 0}>{children}</AnalyticsResult>;
}

interface SentinelBreakdownProps {
  query: SentinelAnalyticsQuery;
  dimension: AnalyticsDimension;
  /** Each tab's breakdowns; the first one per tab decides whether the table is empty. */
  groupBys: Record<AnalyticsDimension, SentinelDimension[][]>;
  keys: readonly SentinelMetricKey[];
  children: (breakdowns: SentinelBreakdownRow[][]) => ReactNode;
}

/** The current tab's breakdowns of `keys`; once they load, the other tab's load in the background so switching is instant. */
export function SentinelBreakdown({ query, dimension, groupBys, keys, children }: SentinelBreakdownProps) {
  const breakdowns = useSentinelBreakdowns(query, groupBys[dimension], keys);
  const loaded = breakdowns.data !== undefined;
  const otherGroupBys = groupBys[dimension === 'category' ? 'channel' : 'category'];
  useEffect(() => {
    if (loaded) prefetchSentinelBreakdowns(query, otherGroupBys, keys);
  }, [loaded, query, otherGroupBys, keys]);
  return <AnalyticsResult result={breakdowns} isEmpty={([rows]) => rows.length === 0}>{children}</AnalyticsResult>;
}

/** Shared shell for every Sentinel Analytics page: account, brand, range and filters, turned into one query. */
export function AnalyticsPage({ title, tabs = false, children }: AnalyticsPageProps) {
  const [selection, setSelection] = useSentinelAnalyticsSelection();
  const [searchParams, setSearchParams] = useSearchParams();
  const dimension: AnalyticsDimension = tabs && searchParams.get('tab') === 'channel' ? 'channel' : 'category';
  const hide = tabs ? dimension : undefined;
  const startDate = useMemo(() => parseISO(selection.startDate), [selection.startDate]);
  const endDate = useMemo(() => parseISO(selection.endDate), [selection.endDate]);
  const rangeError =
    differenceInCalendarDays(endDate, startDate) + 1 > MAX_REPORTING_DAYS ? `Date range must not exceed ${MAX_REPORTING_DAYS} days` : null;
  const accountId = selection.account ? Number(selection.account.id) : null;
  const range = accountId && !rangeError ? { accountId, fromDate: selection.startDate, toDate: selection.endDate, buId: selection.buId } : null;
  // The menus list what the unfiltered range has.
  const options = useSentinelBreakdowns(range, [['category', 'subcategory'], ['channel']], ['sentinelTotalTickets']);
  const filterOptions = useMemo(() => options.data && toFilterOptions(options.data[0], options.data[1]), [options.data]);
  // Picks wait for the options, which say what a category covers and whether every channel is picked.
  const waitingForOptions = !filterOptions && [selection.channels, selection.categories, selection.subCategories].some((picked) => picked.length > 0);
  const filters = toDimensionFilters(selection, filterOptions ?? NO_OPTIONS, hide);
  const tooMany = Object.entries(filters).find(([, values]) => values.length > MAX_FILTER_VALUES);
  const selectionError = rangeError ?? (tooMany ? `Select no more than ${MAX_FILTER_VALUES} ${tooMany[0] === 'subcategory' ? 'sub-categories' : 'categories'}` : null);

  let body: ReactNode;
  if (!accountId) body = <Notice title='Pick an account' detail='Sentinel Analytics shows one account at a time.' />;
  else if (selectionError) body = <p role='alert' className='mt-6 text-sm text-red-600'>{selectionError}</p>;
  else if (waitingForOptions && options.isError) body = <LoadError title='Filters could not be loaded' retry={options.refetch} />;
  else if (!range || waitingForOptions) body = <LoadingState />;
  // Keyed by tab: the other tab's rows have other dimensions, so they must not stand in while this one loads.
  else body = <Fragment key={dimension}>{children({ ...range, filters }, dimension)}</Fragment>;

  return (
    <main className='h-full w-full overflow-y-auto px-4 pt-3.5 pb-3 font-inter'>
      <header className={cn('flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between', tabs ? 'mb-[13px]' : 'mb-[18px]')}>
        <div className='flex items-center gap-1'>
          <Link
            to='/analytics'
            aria-label='Back to Overview'
            title='Back to Overview'
            className='-ml-1.5 flex size-7 shrink-0 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-200 hover:text-ink'
          >
            <ChevronLeft aria-hidden='true' className='size-4' />
          </Link>
          <h1 className='text-base font-semibold leading-6 text-ink'>{title}</h1>
        </div>
        <div aria-label='Analytics filters' className='flex flex-wrap items-center gap-2'>
          <AccountPicker value={selection.account} onChange={(account) => setSelection((s) => ({ ...s, account, buId: null, categories: [], subCategories: [] }))} />
          {accountId ? <BrandScopePicker accountId={accountId} value={selection.buId} onChange={(buId) => setSelection((s) => ({ ...s, buId }))} /> : null}
          <DateRangePicker
            value={{ startDate, endDate }}
            onChange={(next) =>
              next.startDate && next.endDate && setSelection((s) => ({ ...s, startDate: format(next.startDate!, 'yyyy-MM-dd'), endDate: format(next.endDate!, 'yyyy-MM-dd') }))
            }
          />
          <AnalyticsFilters
            selection={selection}
            options={filterOptions}
            disabled={!range || options.isFetching}
            hide={hide}
            onChange={(next) => setSelection((s) => ({ ...s, ...next }))}
          />
        </div>
      </header>
      {tabs ? (
        <div role='tablist' aria-label={`${title} by`} className='flex gap-4'>
          {(['category', 'channel'] as const).map((option) => (
            <button
              key={option}
              type='button'
              role='tab'
              aria-selected={dimension === option}
              onClick={() => setSearchParams((previous) => {
                const next = new URLSearchParams(previous);
                if (option === 'category') next.delete('tab');
                else next.set('tab', option);
                return next;
              }, { replace: true })}
              className={cn(
                '-mb-px border-b py-[11px] text-[13px] leading-[18px] text-gray-800 focus-visible:outline-2 focus-visible:-outline-offset-2',
                dimension === option ? 'border-b-2 border-ink-strong pb-2.5 font-medium text-ink-strong' : 'border-transparent',
              )}
            >
              {option === 'category' ? 'Category' : 'Channel'}
            </button>
          ))}
        </div>
      ) : null}
      {body}
    </main>
  );
}
